import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { isFeatureEnabled } from '@/config/preview-features';
import { adminUsersKeys } from '@/features/admin-users';
import { authKeys } from '@/features/auth';
import { useAuth } from '@/providers/auth-provider';

import { getProfile, updateProfile } from './api';

export const profileKeys = {
  all: ['profile'] as const,
  me: () => [...profileKeys.all, 'me'] as const,
};

/** Every open is a fresh, authoritative load (§3c/§7d/§12c): `gcTime: 0` drops the cache the
 *  moment the modal unmounts, so a reopen is pending (skeleton) rather than a stale seed, and focus
 *  refetch is off so a mid-edit window switch can't reset the form under the admin. */
export function useProfile() {
  return useQuery({
    queryKey: profileKeys.me(),
    queryFn: getProfile,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
}

/** §9b: User Management reads the internal-users list; `authKeys.me` is the login-seeded cache.
 *  The sidebar chip reads `useAuth().user`, not a query, so `<EditProfileModal>` updates it
 *  through `setSession` on success. */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateProfile,
    // Not returned: the modal closes on the PUT alone, never waiting on a mounted list's refetch.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: authKeys.me() });
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
      void queryClient.invalidateQueries({ queryKey: adminUsersKeys.all });
    },
  });
}

/** Grilling Q1: every Dealer delta on the profile surface applies only to a Dealer session while the
 *  `dealers` capability is on (ADR-0034); otherwise the shipped behaviour stands. */
export function useIsDealer(): boolean {
  return useAuth().user?.role === 'dealer' && isFeatureEnabled('dealers');
}
